/**
 * The pages of one scan, as tabs in its header (DEC-194 decision 2).
 *
 * Built from the same rule as every report link (`reportViewsFor`, DEC-190 decision 7),
 * so a tab never offers a view the profile cannot open. The Dependency graph is offered
 * to every profile because its route is open to all (DEC-194 default). Fix plan has no
 * route yet, so it has no tab. Scan settings is the admin lens (`/scans/:id/admin`).
 */
import type { Profile } from '../../types/api'
import type { ScanStatusKey } from '../scans/scanStatus'
import { ownReportView, reportViewsFor } from './reportViews'

export type ScanTabId = 'progress' | 'engineering' | 'executive' | 'product' | 'graph' | 'admin'

/** A scan's status as the navigation sees it: the scan status, or gone (deleted
 *  permanently, or answered 404). */
export type ScanNavStatus = ScanStatusKey | 'deleted'

export interface ScanTab {
  id: ScanTabId
  label: string
  path: (scanId: string) => string
}

const PROGRESS: ScanTab = { id: 'progress', label: 'Progress', path: (id) => `/scans/${id}/monitor` }
const GRAPH: ScanTab = { id: 'graph', label: 'Dependency graph', path: (id) => `/scans/${id}/graph` }
const ADMIN: ScanTab = { id: 'admin', label: 'Scan settings', path: (id) => `/scans/${id}/admin` }

/** The route segment of each tab, for reading the current tab from the URL. */
const SEGMENT_TO_TAB: Record<string, ScanTabId> = {
  monitor: 'progress',
  findings: 'engineering',
  'executive-report': 'executive',
  'product-report': 'product',
  graph: 'graph',
  admin: 'admin',
}

/**
 * The tabs this profile sees on a scan in this status. A deleted scan has none (it opens
 * the deleted notice); a failed or stopped scan has Progress only, since it has no
 * report. An unknown status (the scan has not loaded yet) shows the full set, so the
 * header does not change shape when the scan arrives.
 */
export function scanTabsFor(profile: Profile | null, status: ScanNavStatus | null): ScanTab[] {
  if (status === 'deleted') return []
  if (status === 'failed' || status === 'stopped') return [PROGRESS]
  const views: ScanTab[] = reportViewsFor(profile).map((v) => ({ id: v.id, label: v.label, path: v.path }))
  const isAdmin = profile === null || profile === 'admin'
  return [PROGRESS, ...views, GRAPH, ...(isAdmin ? [ADMIN] : [])]
}

/** The tab a scan page URL shows, or null when the path is not a scan page. */
export function tabFromPath(pathname: string): { scanId: string; tab: ScanTabId } | null {
  const m = /^\/scans\/([^/]+)\/([^/?#]+)/.exec(pathname)
  if (!m) return null
  const tab = SEGMENT_TO_TAB[m[2]]
  return tab ? { scanId: decodeURIComponent(m[1]), tab } : null
}

/**
 * Where a Recently opened item goes: the tab last used in that scan when the profile may
 * still open it, otherwise Progress for a scan that has no report yet (or never will),
 * otherwise the reader's own report view. A deleted scan goes to its own report view,
 * which renders the deleted notice.
 */
export function landingPath(
  scanId: string,
  profile: Profile | null,
  status: ScanNavStatus | null,
  lastTab: ScanTabId | undefined,
): string {
  if (status === 'deleted') return ownReportView(profile).path(scanId)
  const tabs = scanTabsFor(profile, status)
  const last = lastTab ? tabs.find((t) => t.id === lastTab) : undefined
  if (last) return last.path(scanId)
  if (status === 'running' || status === 'paused' || status === 'queued' || status === 'scheduled' || status === 'failed' || status === 'stopped') {
    return PROGRESS.path(scanId)
  }
  return ownReportView(profile).path(scanId)
}
